/* Loaded once, outside the replaceable drawer HTML. */
(() => {
  if (customElements.get('cart-package-protection')) return;
  const root = window.Shopify?.routes?.root || '/';
  const sectionIDs = ['cart-drawer', 'cart-icon-bubble'];
  let running = false;
  let queued = false;
  let applying = false;
  let preference = null;
  let failed = false;
  let timer;
  const controlStates = new Map();
  const widget = () => document.querySelector('cart-drawer cart-package-protection');

  function readState(scope) {
    const script = scope?.querySelector('[data-protection-state]');
    if (!script) throw new Error('Package protection markup is missing.');
    return JSON.parse(script.textContent);
  }

  function plan(state, requestedPreference) {
    const declined = requestedPreference === null ? state.declined : !requestedPreference;
    const wanted = state.amount > 0 && !declined;
    if (wanted && !state.variant) {
      throw new Error('Package protection is unavailable for this cart. Uncheck protection to continue without it, or contact the store.');
    }
    const target = wanted ? state.variant : 0;
    const updates = {};
    const correct = target && state.lines.length === 1 &&
      state.lines[0].variant === target && state.lines[0].quantity === 1;
    if (!correct) {
      state.lines.forEach(line => { updates[line.key] = 0; });
      // update.js supports adding a variant not currently in the cart.
      if (target) {
        const existingTarget = state.lines.find(line => line.variant === target);
        updates[existingTarget ? existingTarget.key : target] = 1;
      }
    }
    const attributes = {};
    const insurance = target ? 'true' : 'false';
    if (state.insurance !== insurance) attributes.insurance = insurance;
    if (requestedPreference !== null) attributes['decline-insurance'] = declined ? 'declined' : 'not-declined';
    return { updates, attributes };
  }

  function status(message = '', retry = false) {
    const el = widget();
    if (!el) return;
    if (message) el.hidden = false;
    const text = el.querySelector('[data-protection-status]');
    text.textContent = message;
    text.hidden = !message;
    el.querySelector('[data-protection-retry]').hidden = !retry;
  }

  function lock(busy) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.toggleAttribute('aria-busy', busy);
    if (busy) {
      drawer.querySelectorAll('button:not(.drawer__close), input[type="number"], [data-protection-checkbox], select').forEach(el => {
        if (!controlStates.has(el)) controlStates.set(el, el.disabled);
        el.disabled = true;
      });
    } else {
      controlStates.forEach((disabled, el) => { if (el.isConnected) el.disabled = disabled; });
      controlStates.clear();
      // Allow opting out even when the required price tier is unavailable.
      const box = widget()?.querySelector('[data-protection-checkbox]');
      if (box && failed) box.disabled = false;
    }
  }

  async function request(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Unable to update package protection. Please retry.');
    return data;
  }

  async function sections() {
    const url = new URL(root + 'cart', location.origin);
    url.searchParams.set('sections', sectionIDs.join(','));
    return request(url.toString(), { cache: 'no-store' });
  }

  function parseDrawer(html) {
    if (!html) throw new Error('Unable to refresh the cart drawer. Please retry.');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const inner = parsed.querySelector('#CartDrawer .drawer__inner');
    if (!inner) throw new Error('Cart drawer markup could not be refreshed.');
    return { parsed, inner, state: readState(inner) };
  }

  function render(rendered) {
    const { parsed, inner } = parseDrawer(rendered['cart-drawer']);
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const focused = drawer.contains(document.activeElement) ? document.activeElement : null;
    const focusID = focused?.id;
    const focusCheckbox = focused?.matches('[data-protection-checkbox]');
    const scroll = drawer.querySelector('.drawer__contents')?.scrollTop || 0;
    applying = true;
    try {
      // Preserve #CartDrawer, its overlay listener, and the drawer open state.
      drawer.querySelector('.drawer__inner').replaceWith(inner);
      drawer.classList.toggle('is-empty', parsed.querySelector('cart-drawer').classList.contains('is-empty'));
      if (rendered['cart-icon-bubble']) {
        const bubble = new DOMParser().parseFromString(rendered['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        const current = document.getElementById('cart-icon-bubble');
        if (bubble && current) current.innerHTML = bubble.innerHTML;
      }
    } finally { applying = false; }
    const contents = drawer.querySelector('.drawer__contents');
    if (contents) contents.scrollTop = scroll;
    if (focused && drawer.classList.contains('active')) {
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const target = (focusID && document.getElementById(focusID)) ||
        (focusCheckbox && widget()?.querySelector('[data-protection-checkbox]')) || drawer.querySelector('.drawer__close');
      if (typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), target);
      else target?.focus({ preventScroll: true });
    }
    if (typeof initMobileCartRecommendations === 'function') initMobileCartRecommendations();
    const items = drawer.querySelector('cart-drawer-items');
    items?.updateCartUpsellToggleState?.();
    items?.updateCartUpsellVisibility?.();
  }

  function schedule() {
    if (applying || !widget()) return;
    queued = true;
    lock(true);
    clearTimeout(timer);
    if (!running) timer = setTimeout(sync, 0);
  }

  async function sync() {
    if (running) return;
    running = true;
    failed = false;
    try {
      while (queued) {
        queued = false;
        status('Updating package protection…');
        // Read freshly rendered Liquid: product tags are not supplied by cart.js JSON.
        const rendered = await sections();
        const state = parseDrawer(rendered['cart-drawer']).state;
        const wantedPreference = preference;
        const changes = plan(state, wantedPreference);
        if (Object.keys(changes.updates).length || Object.keys(changes.attributes).length) {
          const updated = await request(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...changes, sections: sectionIDs, sections_url: root + 'cart' })
          });
          if (preference === wantedPreference) preference = null;
          render(updated.sections?.['cart-drawer'] ? updated.sections : await sections());
        }
        lock(true);
      }
      status();
    } catch (error) {
      failed = true;
      queued = false;
      status(error.message, true);
      console.error('[Package protection]', error);
    } finally {
      running = false;
      lock(false);
      if (queued) schedule();
    }
  }

  class CartPackageProtection extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-protection-checkbox]').addEventListener('change', event => {
        preference = event.target.checked;
        schedule();
      });
      this.querySelector('[data-protection-retry]').addEventListener('click', schedule);
      schedule();
    }
  }
  customElements.define('cart-package-protection', CartPackageProtection);

  // Also guard Enter-key form submission while a protection update is pending.
  document.addEventListener('submit', event => {
    if (event.target.id !== 'CartDrawer-Form' || (!running && !queued && !failed)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    status(failed ? 'Please retry the protection update before checking out.' : 'Updating package protection…', failed);
  }, true);
  document.addEventListener('click', event => {
    if (!event.target.closest('#CartDrawer-Checkout') || (!running && !queued && !failed)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    status(failed ? 'Please retry the protection update before checking out.' : 'Updating package protection…', failed);
  }, true);
})();
